import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.12, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroFrame = ({ mx, my, t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  return (
    <motion.div ref={ref} style={{ y }} className="relative [perspective:1400px]">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ duration: 1.5, delay: 0.6, ease: EASE }}
          className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden rounded-[28px] bg-[#132D54]"
        >
          <motion.img
            src="/img/hero.jpg"
            alt="Infrastructure réseau"
            style={{ y: imgY, scale: 1.18 }}
            className="absolute inset-0 h-full w-full object-cover saturate-[0.85]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A] via-[#0B1F3A]/20 to-transparent" />
          <div className="absolute inset-0 mix-blend-color bg-[#0B1F3A]/40" />
          <p className="absolute left-5 bottom-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200">{t.hero.frame}</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    mx.set(px - 0.5);
    my.set(py - 0.5);
    e.currentTarget.style.setProperty("--mx", `${px * 100}%`);
    e.currentTarget.style.setProperty("--my", `${py * 100}%`);
  };
  return (
    <section onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden grain pt-28 pb-16 lg:pt-32" data-testid="hero-section">
      <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7" key={lang}>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-xs uppercase tracking-[0.22em] text-[#2DD4BF] flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="inline-block w-2 h-2 rounded-full bg-[#2DD4BF] animate-pulse" />
            {t.hero.eyebrow}
          </motion.p>
          <h1 className="mt-7 font-extrabold uppercase stretch-condensed leading-[0.86] tracking-[-0.02em] text-[15vw] sm:text-[12vw] lg:text-[7.4vw] 2xl:text-[112px]" data-testid="hero-title">
            {t.hero.lines.map((l, i) => (
              <MaskLine key={l} i={i}>{l}</MaskLine>
            ))}
            <MaskLine i={3} className="font-serif-i normal-case text-[#2DD4BF] tracking-normal stretch-wide">{t.hero.accent}</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.9, ease: EASE }} className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            {t.hero.lead}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease: EASE }} className="mt-9 flex flex-wrap gap-3">
            <button data-testid="hero-cta-services" onClick={() => scrollToId("services")} className="group inline-flex items-center gap-3 rounded-full bg-[#2DD4BF] text-[#0B1F3A] pl-6 pr-2 py-2 font-semibold hover:bg-white transition-colors">
              {t.hero.cta}
              <span className="grid place-items-center w-9 h-9 rounded-full bg-[#0B1F3A] text-[#2DD4BF] transition-transform duration-500 group-hover:rotate-[-45deg]"><ArrowRight size={16} /></span>
            </button>
            <button data-testid="hero-cta-join" onClick={() => scrollToId("join")} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-medium hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-colors">
              {t.hero.cta2} <ArrowDownRight size={16} />
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 px-4 sm:px-10 lg:px-0">
          <HeroFrame mx={mx} my={my} t={t} />
        </div>
      </div>
    </section>
  );
};
